import * as React from 'react';
import * as _ from 'underscore';

import { IGlob } from '../ts/Glob';
import { config } from '../ts/config';
import StringField from '../../../_common/platform/tsx/StringField';
import BooleanField from '../../../_common/platform/tsx/BooleanField';

import { DirsTree } from './DirsTree';
import { DirsTemplatesSingle } from './DirsTemplatesSingle';

import { useOverflowHook } from '../../../_common/platform/tsx/mixins/OverflowMixin';
import { getStyles, useResizeAndMoveHook } from '../../../_common/platform/tsx/mixins/ResizeMove';

const DEFAULT_ID = 'defaultTemplates'; // 'default' is used for WS.

export interface IDirsTemplatesInnerProperties {
    id?;
    name?: string;
    title?: string;
    overwrite?: boolean;
    allowSelectItem?;
    selectTitle?: string;
    nameSelect?: boolean;
    overwriteShow?: boolean;
    selectMode?: boolean;
    selectTemplateMode?: boolean;
    zIndex?: number;
    disableOverflow?: boolean;
    glob?: IGlob;
    onHide?(e): void;
    onTemplateSelect?(templateSelect, templateSelectData): void;
    onDirSelect?(id, name: string, overwrite: boolean): void;
}

interface IState {
    templateSelect?;
    templateSelectData?;
    name?: string;
}

function getInstanceState(id: string, glob: IGlob) {
    return { ...glob.TemplatesDirsStore.getInstanceState(id ?? DEFAULT_ID) };
}

export function DirsTemplatesInner(props: IDirsTemplatesInnerProperties) {
    const [instanceState, setInstanceState] = React.useState(getInstanceState(props.id, props.glob));
    const [dirSelect, setDirSelect] = React.useState(config.TEMPLATES_ROOT_DIR_ID);
    const [overwrite, setOverwrite] = React.useState(props.overwrite ?? false);
    const [state, setState] = React.useState<IState>({
        templateSelect: null,
        templateSelectData: null,
        name: props.name
    });

    const containerRef = React.useRef<HTMLDivElement>(null);
    const contentRef = React.useRef<HTMLDivElement>(null);
    const headerRef = React.useRef<HTMLDivElement>(null);
    const footerRef = React.useRef<HTMLDivElement>(null);

    const { containerSize, initialContainerHeight, initialContentWidth, position } = useResizeAndMoveHook(
        {
            dialogName: 'FolderTemplates',
            draggable: true,
            resizable: true,
            disableOverflow: props.disableOverflow
        },
        'auto',
        '1/2',
        contentRef,
        headerRef,
        footerRef
    );
    const containerHeight = useOverflowHook(
        props.disableOverflow,
        containerSize.height,
        containerRef,
        headerRef,
        footerRef
    );

    React.useEffect(() => {
        const handleOnStoreChange = () => {
            setInstanceState(getInstanceState(props.id, props.glob));
        };

        props.glob.TemplatesDirsStore.addChangeListener(handleOnStoreChange);

        const id = props.id ?? DEFAULT_ID;

        props.glob.AppActions.templatesExplorerClear(id);
        props.glob.AppActions.templatesExplorerSingleLoad(dirSelect, id);

        return () => {
            props.glob.TemplatesDirsStore.removeChangeListener(handleOnStoreChange);
        };
    }, [props.id]);

    const handleOnHide = (e) => {
        if (typeof props.onHide === 'function') {
            props.onHide(e);
        } else {
            props.glob.mainRouter.navigate('/', { trigger: true });
        }
    };

    const handleOnDirSelect = (id: number) => {
        selectTemplate(null, null);
        setDirSelect(id);
        props.glob.AppActions.templatesExplorerSingleLoad(id, props.id ?? DEFAULT_ID);
    };

    const handleOnTemplateSelect = () => {
        if (typeof props.onTemplateSelect === 'function') {
            props.onTemplateSelect(state.templateSelect, state.templateSelectData);
        }
    };

    const selectTemplate = (id, data, name?: string) => {
        if (name != null) {
            setState({
                ...state,
                templateSelect: id,
                templateSelectData: data,
                name: name //VR: save it too; it means: selecting T item pasted its name to 'NAME" field.
            });
        } else {
            setState({
                ...state,
                templateSelect: id,
                templateSelectData: data
            });
        }
    };

    let selectTemplateButton: React.JSX.Element;
    let selectDirButton: React.JSX.Element;

    if (state.templateSelect != null && props.selectTemplateMode) {
        selectTemplateButton = (
            <button type="button" className="btn btn-success" onClick={handleOnTemplateSelect}>
                {props.selectTitle || 'Create widget from this workspace'}
            </button>
        );
    }

    if (dirSelect != null && props.selectMode) {
        const processOnSelect = (id: number) => {
            if (typeof props.onDirSelect === 'function') {
                props.onDirSelect(id || dirSelect, state.name, overwrite);
            }
        };

        const checkIfNameExists = (name: string) => {
            if (overwrite || name == null) {
                // VR: if User set 'overwrite ON", then don't make searching!
                return null;
            }

            return _.find(instanceState.dirContent, (obj) => {
                return obj.name === name;
            });
        };

        const handleOnCreateButtonClick = () => {
            if (state.name != null && state.name === '') {
                props.glob.AppActions.showNotification_Fail('Workspace name can not be empty');
                return;
            }

            let existing = checkIfNameExists(state.name);
            let removeDef = $.Deferred();
            let interval;

            if (existing != null) {
                props.glob.DialogActions.createDialog(
                    `Overwrite workspace "${state.name}"`,
                    `Workspace with name "${state.name}" already exists in selected folder. Do you want to overwrite it?`,
                    () => {},
                    () => {
                        setOverwrite(true);
                        // VR: we need go out of this dialog and afterwards: set def.resolve() to close this dialog:
                        interval = setInterval(() => {
                            removeDef.resolve();
                        }, 200);
                    },
                    () => {},
                    'confirm',
                    {
                        confirmTitle: null,
                        cancelTitle: 'Overwrite'
                    }
                );

                removeDef.done(() => {
                    clearInterval(interval);
                    processOnSelect(dirSelect);
                });

                return;
            }

            processOnSelect(dirSelect);
        };

        selectDirButton = (
            <button type="button" className="btn btn-success" onClick={handleOnCreateButtonClick}>
                {props.selectTitle || 'Create template in selected folder'}
            </button>
        );
    }

    let nameSelect: React.JSX.Element;

    if (props.nameSelect) {
        nameSelect = (
            <div className="name-select col-8">
                <StringField
                    value={state.name}
                    onChange={(value: string) => {
                        setState({
                            ...state,
                            name: value
                        });
                    }}
                />
            </div>
        );
    }

    let overwriteCheckbox: React.JSX.Element;
    let createSubFolder: React.JSX.Element;
    let className = '';

    if (props.overwriteShow) {
        className = ' modal-header-narrow';

        overwriteCheckbox = (
            <div className="overwrite-checkbox col-4">
                <BooleanField
                    value={overwrite}
                    title={'Replace Existing'}
                    onChange={(value: boolean) => {
                        setOverwrite(value);
                    }}
                />
            </div>
        );

        const handleOnNewFolderClick = () => {
            if (dirSelect == null) {
                props.glob.AppActions.showNotification_Success('Select a folder for operation, please');
                return;
            }

            props.glob.AppActions.dirsCreateStart(dirSelect, props.id ?? DEFAULT_ID);
        };

        createSubFolder = (
            <div
                key={'new-dir-button'}
                className="add-button btn btn-default btn-xs bar-btn sub-dir-button"
                onClick={handleOnNewFolderClick}
            >
                <span className="glyphicon glyphicon-plus"></span>
                &nbsp;New Folder&nbsp;
            </div>
        );
    }

    const { modalStyle, containerStyle, contentStyle, bodyStyle } = getStyles(
        initialContainerHeight,
        initialContentWidth,
        containerSize.height,
        containerHeight,
        position,
        config.LAYOUT_TYPE === 'narrow',
        undefined,
        props.zIndex
    );

    return (
        <div className="modal fade show popup-dirs" style={modalStyle} role="dialog">
            <div className="modal-dialog" style={containerStyle} ref={containerRef}>
                <div className="modal-content" style={contentStyle} ref={contentRef}>
                    <div className={'modal-header' + className} ref={headerRef}>
                        <h4 className="modal-title">{props.title ?? 'Templates explorer'}</h4>

                        {createSubFolder}

                        <button type="button" className="close" onClick={handleOnHide}>
                            &times;
                        </button>
                    </div>

                    <div className="modal-body container-fluid" style={bodyStyle}>
                        <div className="row">
                            <div className="col-sm-4">
                                <DirsTree
                                    id={props.id ?? DEFAULT_ID}
                                    rootId={config.TEMPLATES_ROOT_DIR_ID}
                                    rootName={config.TEMPLATES_ROOT_DIR_NAME}
                                    collapsed={false}
                                    select={dirSelect}
                                    glob={props.glob}
                                    onSelect={handleOnDirSelect}
                                />
                            </div>

                            <div className="col-sm-8">
                                <DirsTemplatesSingle
                                    content={instanceState.dirContent}
                                    loading={instanceState.loading}
                                    select={state.templateSelect}
                                    allowSelect={props.allowSelectItem}
                                    glob={props.glob}
                                    onSelect={selectTemplate}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer" ref={footerRef}>
                        <div className="row flex-grow-1 align-items-center">
                            {nameSelect}
                            {overwriteCheckbox}
                        </div>

                        {selectDirButton}
                        {selectTemplateButton}

                        <button type="button" className="btn btn-default" onClick={handleOnHide}>
                            {'Cancel'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
